import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { MacroMarkdownEditorField } from "../fields/MacroMarkdownEditorField";
import { useRenderTemplateWithCollections } from "../MacroBindingContext";


export interface MacroMarkdownBlockProps {
  content: string;
  className?: string;
}

const MacroMarkdownBlock = {
  label: "Markdown",
  llmDescription: `A content block that renders PURE Markdown syntax with variable substitution.
- 'content' (string): Markdown text ONLY - no HTML allowed
- 'className' (string, optional): Additional CSS classes for spacing/layout
- Supports {{variable}} placeholders that get substituted with binding values
- Inside repeaters, also supports collection bindings like {{program.Name}}, {{room.RoomType}}, {{pos.Description}}

CRITICAL - NO HTML ALLOWED IN THIS BLOCK:
- DO NOT use <u>, <strong>, <em>, <br>, <a>, or ANY HTML tags
- HTML tags will be rendered as literal text, not interpreted
- If you need underlines (<u>), use the "Html" block instead
- If you need complex formatting, use the "Html" block instead

MARKDOWN SYNTAX ONLY:
- **bold** or __bold__ (NOT <strong> or <b>)
- *italic* or _italic_ (NOT <em> or <i>)
- ~~strikethrough~~
- [link text](url) (NOT <a href="">)
- Line breaks: Use two spaces at end of line or blank line for new paragraph

WHEN TO USE Html BLOCK INSTEAD:
- Need underlines (<u>text</u>)
- Need <br> line breaks
- Need inline HTML links with attributes (target="_blank", etc.)
- Need any HTML formatting not supported by Markdown

Example content (correct):
\`\`\`
Hi **{{FirstName}}**,

Thank you for your inquiry! We're excited to help you on your **fitness journey**.

*"Our Goal is to Help You Achieve Yours, {{FirstName}}!"*
\`\`\`

Example of what NOT to do (use Html block instead):
\`\`\`
After all, **<u>This is what we do!</u>**  <-- WRONG, use Html block
Click <a href="...">here</a>  <-- WRONG, use [here](url) or Html block
\`\`\``,
  fields: {
    content: {
      type: "custom" as const,
      label: "Markdown Content",
      render: ({ name, field, value, onChange }: any) => (
        <MacroMarkdownEditorField
          name={name}
          field={field}
          value={value as string | undefined}
          onChange={onChange}
        />
      ),
    },
    className: {
      type: "text" as const,
      label: "CSS Class (optional)",
    },
  },
  defaultProps: {
    content: `Hi **{{FirstName}}**,

Thank you for your inquiry!

We're excited to help you with your **{{ProgramDaysText}}** program.`,
    className: "",
  },
  render: (props: MacroMarkdownBlockProps) => {
    const { content, className } = props;
    const render = useRenderTemplateWithCollections();

    // Substitute variables in the markdown content
    const substitutedContent = render(content || "");

    // Base prose classes for nice typography
    const baseClass =
      "macro-markdown-content prose prose-sm max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0";
    const extraClass = typeof className === "string" ? className.trim() : "";
    const mergedClass = [baseClass, extraClass].filter(Boolean).join(" ");

    return (
      <div className={mergedClass}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          // Allow raw HTML in markdown for inline formatting like <strong>, <br>, etc.
          rehypePlugins={[]}
          components={{
            // Custom link component to handle external links
            a: ({ href, children, ...props }) => (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#0066cc", textDecoration: "underline" }}
                {...props}
              >
                {children}
              </a>
            ),
          }}
        >
          {substitutedContent}
        </ReactMarkdown>
      </div>
    );
  },
};

export default MacroMarkdownBlock;
